import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";

export interface KpiWeekPoint {
  week: string;
  avg_cost: number;
  story_count: number;
  target: number;
}

export interface OutlierStory {
  work_item_ref: string;
  cost_usd: number;
  dominant_phase: string | null;
}

export interface KpiTrendData {
  weeks: KpiWeekPoint[];
  trend: "improving" | "stable" | "degrading";
  overhead_ratio: number;
  outliers: OutlierStory[];
  current_avg: number | null;
}

export function useKpiTrend(weeks: number = 4, projectSlug?: string | null) {
  const query = useQuery({
    queryKey: queryKeys.metrics.kpiTrend(weeks, projectSlug ?? null),
    queryFn: () => {
      const params = new URLSearchParams({ weeks: String(weeks) });
      if (projectSlug?.trim()) params.set("project", projectSlug.trim());
      return api<KpiTrendData>(`/api/telemetry/kpi-trend?${params.toString()}`);
    },
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  // Original hook rethrew the underlying Error unchanged (only wrapping
  // non-Error rejections in a generic message) — api() always throws an
  // Error subclass, so this is effectively a message pass-through.
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Failed to load KPI trend"
      : null,
    refetch,
  };
}
